import { toSentenceCase } from "#imports";
import { rotate } from "three/src/nodes/utils/RotateNode.js";

const { showErrorToast, showInfoToast, showWarningToast } = useNotification();

type ApiError = {
  statusCode: number;
  message?: string;
  data?: {
    message?: string;
    errors?: Record<string, string[]>;
  };
};

const isApiError = (error: unknown): error is ApiError => {
  console.log(error)
  return typeof error === "object" && error !== null && "statusCode" in error;
};

export const handleError = (error: unknown, context?: string) => {
  if (!isApiError(error)) {
    showErrorToast(
      "Something Went Wrong",
      context && import.meta.dev
        ? `We ran into a problem while ${context}. Please try again.`
        : "We ran into a problem. Please try again.",
    );
    return;
  }

  const { clearAuthState } = useAuth();
  const statusCode = error.statusCode;
  const route = useRoute();

  // --- 1. CSRF / SECURITY (419 or 403 with csrf in message) ---
  const isCsrfError =
    statusCode === 419 ||
    (statusCode === 403 && error.data?.message?.toLowerCase().includes("csrf"));

  if (isCsrfError) {
    showWarningToast("Security Refresh", "Synchronizing security tokens...");
    if (import.meta.client) {
      setTimeout(() => window.location.reload(), 1000);
    }
    return;
  }

  if (statusCode === 401 && route.path !== "/login") {
    clearAuthState();
    showInfoToast(
      "Session Ended",
      "Please sign in again to continue watching.",
    );
    if (import.meta.client) {
      navigateTo("/", { replace: true });
    }
    return;
  }

  // --- 3. STANDARD ERROR CONFIG ---
  const getErrorConfig = () => {
    // Silent codes — never expose internals to the user
    const silentCodes = new Set([400, 500, 503]);

    if (silentCodes.has(statusCode)) {
      console.error(
        `[Client] ${statusCode} error${context ? ` while ${context}` : ""}:`,
        error,
      );
      return {
        title: "Something Went Wrong",
        message: "We ran into a problem. Please try again in a moment.",
      };
    }

    // User-actionable codes
    const titles: Record<number, string> = {
      401: "Invalid Credentials",
      403: "Content Unavailable",
      404: "Not Found",
      422: "Please Check Your Details",
      429: "Slow Down",
    };

    const genericMessages: Record<number, string> = {
      401: "Your credentials are incorrect. Please try again.",
      403:
        error.data?.message ||
        "This content isn't available on your plan or in your region.",
      404:
        error.data?.message ||
        "The content you're looking for doesn't exist or has been removed.",
      429: "You're doing that too fast. Please wait a moment and try again.",
    };

    const friendlyTitle = titles[statusCode] || "Something Went Wrong";
    const defaultMessage =
      genericMessages[statusCode] || "Something went wrong. Please try again.";

    // 422 and 401 (on login page) may carry field-level errors from Laravel
    if ((statusCode === 422 || statusCode === 401) && error.data?.errors) {
      const fieldErrors = Object.entries(error.data.errors)
        .map(([_, messages]) => messages.join(", "))
        .join(" ");

      return {
        title: friendlyTitle,
        message: fieldErrors || error.data?.message || defaultMessage,
      };
    }

    return {
      title: friendlyTitle,
      message: error.data?.message || defaultMessage,
    };
  };

  const { title, message } = getErrorConfig();
  showErrorToast(title, toSentenceCase(message));
};
